/**
 * Audit Explorer Configuration
 *
 * Read-only explorer for browsing the audit_log table.
 * Admin-only access. Uses ConfigurableBaseExplorer pattern.
 *
 * Filters are managed by CBE's built-in filter system via quickFilters[].
 * The service adapter reads filter values from QueryParams.filters[].
 */
import {
  BaseExplorerConfig,
  EntityServiceAdapter,
  FieldTransformer,
  QueryParams,
  QueryResult,
  ExplorerFeatureConfig,
  PresentationConfig,
} from '../../../types/baseExplorerConfig';
import { fetchAuditLogs } from '../../../services/auditService';
import { backendPage } from '../../../utils/pagination';
import type { AuditLogEntry, AuditFetchParams } from '../../../services/auditService';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { AUDIT_COLUMNS } from '../../../shared/auditSchema';
import { gridColumns } from '../../../shared/columnSchema';

// ─── Filter option lists (shared with quickFilters config) ────────────────────

// export: every AFF download and CSV export is audited as one (#866 M5).
const ACTION_OPTIONS = ['read', 'create', 'update', 'delete', 'export'];
const FEATURE_OPTIONS = [
  'wildfires', 'var', 'duty_logs', 'stations', 'weather',
  'modelling', 'gis', 'layer_manager', 'lightning', 'support', 'aff',
];
const ROLE_OPTIONS = [
  'admin', 'dev', 'user',
  'HQ', 'Inuvik', 'South Slave', 'Sahtu', 'Deh Cho', 'North Slave', 'WBNP',
  'RDO', 'TDO', 'MOF', 'superintendent', 'Director',
  'advanced_gis', 'simple_gis', 'simple_modelling', 'advanced_modelling',
  'limited_station_mutation', 'advanced_weather', 'advanced_vars',
  'basic_admin', 'Clerk',
].sort();
const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

// ─── Service Adapter ──────────────────────────────────────────────────────────

const serviceAdapter: EntityServiceAdapter<any> = {
  async fetchData(params: QueryParams): Promise<QueryResult<any>> {
    // Audit backend uses 0-based pages; CBE sends 1-based currentPage.
    const { page, limit } = backendPage(params);
    const apiParams: AuditFetchParams = {
      page: page - 1,
      limit,
    };

    // Read filters from QueryParams.filters[] (set by CBE's filter system)
    if (params?.filters) {
      for (const f of params.filters) {
        switch (f.field) {
          case 'dateRange': {
            // CBE decomposes dateRange into two FilterParams with operator gte/lte
            if (f.operator === 'gte') apiParams.startDate = f.value as string;
            if (f.operator === 'lte') apiParams.endDate = f.value as string;
            break;
          }
          case 'user':
            apiParams.user = f.value as string;
            break;
          case 'action':
            apiParams.action = f.value as string;
            break;
          case 'feature':
            apiParams.feature = f.value as string;
            break;
          case 'role':
            apiParams.role = f.value as string;
            break;
          case 'method':
            apiParams.method = f.value as string;
            break;
          case 'endpoint':
            apiParams.endpoint = f.value as string;
            break;
          case 'entityId':
            apiParams.entityId = f.value as string;
            break;
          case 'context':
            (apiParams as any).context = f.value as string;
            break;
          case 'ip_address':
            (apiParams as any).ip_address = f.value as string;
            break;
          case 'payload':
            (apiParams as any).payload = f.value as string;
            break;
        }
      }
    }

    // Search from base explorer search bar
    if (params?.search?.term) {
      apiParams.search = params.search.term;
    }

    // Sort from base explorer column headers
    if (params?.sort && params.sort.length > 0) {
      apiParams.sortField = params.sort[0].field;
      apiParams.sortDirection = params.sort[0].direction;
    }

    const result = await fetchAuditLogs(apiParams);

    const data = result.data.map((row: AuditLogEntry) => ({
      ...row,
      _id: row.id,
    }));

    return {
      data,
      total: result.total,
      page: result.page,
      limit: result.limit,
      hasMore: result.hasMore,
    };
  },
};

// ─── Field Transformers ──────────────────────────────────────────────────────

const timestampTransformer: FieldTransformer = {
  transform: (value: any) => {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleString('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  },
};

const rolesTransformer: FieldTransformer = {
  transform: (value: any) => {
    if (!value || !Array.isArray(value)) return '';
    return value.join(', ');
  },
};

const actionTransformer: FieldTransformer = {
  transform: (value: any) => value ? String(value).toUpperCase() : '',
};

const nullDashTransformer: FieldTransformer = {
  transform: (value: any) => value ?? '-',
};

const payloadTransformer: FieldTransformer = {
  transform: (value: any) => {
    if (!value) return '-';
    try {
      const str = typeof value === 'string' ? value : JSON.stringify(value);
      return str.length > 80 ? str.substring(0, 80) + '...' : str;
    } catch {
      return '[payload]';
    }
  },
};

const AUDIT_FIELD_TRANSFORMERS: Record<string, FieldTransformer> = {
  created_at: timestampTransformer,
  roles: rolesTransformer,
  action: actionTransformer,
  context: nullDashTransformer,
  entity_id: nullDashTransformer,
  ip_address: nullDashTransformer,
  payload: payloadTransformer,
};

// ─── Features ─────────────────────────────────────────────────────────────────

const AUDIT_FEATURES: ExplorerFeatureConfig = {
  pagination: {
    enabled: true,
    defaultPageSize: 25,
    pageSizeOptions: [25, 50, 100],
    showPageInfo: true,
  },
  sorting: {
    enabled: true,
    multiColumn: false,
    defaultSort: [{ field: 'created_at', direction: 'desc' }],
  },
  filtering: {
    enabled: true,
    quickFilters: [
      {
        field: 'dateRange',
        label: 'Date Range',
        type: 'dateRange',
      },
      {
        field: 'user',
        label: 'User',
        type: 'textSearch',
      },
      {
        field: 'action',
        label: 'Action',
        type: 'dropdown',
        options: ACTION_OPTIONS.map(a => ({ value: a, label: a.toUpperCase() })),
      },
      {
        field: 'role',
        label: 'Role',
        type: 'dropdown',
        options: ROLE_OPTIONS.map(r => ({ value: r, label: r })),
      },
      {
        field: 'feature',
        label: 'Feature',
        type: 'dropdown',
        options: FEATURE_OPTIONS.map(f => ({ value: f, label: f.replace('_', ' ') })),
      },
      {
        field: 'method',
        label: 'Method',
        type: 'dropdown',
        options: METHOD_OPTIONS.map(m => ({ value: m, label: m })),
      },
      {
        field: 'endpoint',
        label: 'Endpoint',
        type: 'textSearch',
      },
      {
        field: 'entityId',
        label: 'Entity ID',
        type: 'textSearch',
      },
      {
        field: 'context',
        label: 'Context',
        type: 'textSearch',
      },
      {
        field: 'ip_address',
        label: 'IP Address',
        type: 'textSearch',
      },
      {
        field: 'payload',
        label: 'Payload',
        type: 'textSearch',
      },
    ],
    advancedFilters: false,
  },
  searching: {
    enabled: true,
    placeholder: 'Search by username...',
    searchFields: ['username'],
    debounceMs: 300,
  },
  editing: {
    enabled: false,
    mode: 'modal',
    allowCreate: false,
    allowUpdate: false,
    allowDelete: false,
  },
  export: {
    enabled: true,
    formats: ['csv'],
    includeFilters: true,
    entity: 'audit'
  },
};

// ─── Cell renderers (frontend-only, layered onto AUDIT_COLUMNS) ──────────────

const AUDIT_CELL_RENDERERS: Record<string, { type: 'custom'; options: { render: (value: any) => string } }> = {
  created_at: {
    type: 'custom',
    options: {
      render: (value: any) => {
        if (!value) return '';
        const d = new Date(value);
        return d.toLocaleString('en-CA', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      },
    },
  },
  roles: {
    type: 'custom',
    options: {
      render: (value: any) => (!value || !Array.isArray(value)) ? '' : value.join(', '),
    },
  },
  action: {
    type: 'custom',
    options: {
      render: (value: any) => {
        const colorMap: Record<string, string> = {
          create: '#28a745',
          read: '#6c757d',
          update: '#fd7e14',
          delete: '#dc3545',
          export: '#6f42c1',
        };
        const color = colorMap[value] || '#6c757d';
        return `<span style="color:${color};font-weight:600;text-transform:uppercase">${value}</span>`;
      },
    },
  },
  entity_id: {
    type: 'custom',
    options: { render: (value: any) => value || '-' },
  },
  payload: {
    type: 'custom',
    options: {
      render: (value: any) => {
        if (!value) return '-';
        try {
          const str = typeof value === 'string' ? value : JSON.stringify(value);
          return str.length > 80 ? str.substring(0, 80) + '...' : str;
        } catch {
          return '[payload]';
        }
      },
    },
  },
};

// ─── Presentation ─────────────────────────────────────────────────────────────

const AUDIT_PRESENTATION: PresentationConfig = {
  title: 'Audit Log',
  subtitle: 'Browse system activity log',
  icon: faClipboardList,

  columnConfig: gridColumns(AUDIT_COLUMNS).map((c) => {
    const renderer = AUDIT_CELL_RENDERERS[c.field];
    return renderer ? { ...c, cellRenderer: renderer } : c;
  }),
  layout: {
    minHeight: 400,
    responsive: true,
    showToolbar: true,
    showFooter: true,
  },
};

// ─── Exported config ──────────────────────────────────────────────────────────

const auditExplorerConfig: BaseExplorerConfig<any> = {
  entityName: 'Audit Entry',
  complexityLevel: 'moderate',
  displayFields: [
    'created_at', 'username', 'roles', 'action', 'context', 'feature',
    'method', 'endpoint', 'entity_id',
  ],
  fieldTransformers: AUDIT_FIELD_TRANSFORMERS as any,
  serviceAdapter: serviceAdapter as any,
  features: AUDIT_FEATURES,
  presentation: AUDIT_PRESENTATION,
};

export default auditExplorerConfig;
